import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/fi';
import { Customer, Order } from '../../types/customer';
import apartmentIcon from './icons/home.svg';
import apartmentIconPyha from './icons/home-blue.svg';
import Button from '../../components/Button';
import Link from '../../components/Link';
import ImageAccordion from '../../components/ImageAccordion';
import { getDrupalLink, getDomain } from '../../utils/functions';
import FellowPassengers from '../../components/FellowPassenger';
import { iTicketWidget } from '../../types/drupal';

interface Props {
  order: Order;
  oldReservation: boolean;
  domain: string;
  customerData: Customer;
  idToken: string;
  iTicketWidgets?: iTicketWidget[] | null;
}

const ReservationItem: React.FC<Props> = (props: Props): JSX.Element => {
  const { order } = props;
  const { t, i18n } = useTranslation(['bookings', 'common']);
  const { language } = i18n;
  const domain = getDomain();

  dayjs.locale('fi');

  const endTime = dayjs(order.DateTimeEnd);

  // Reservations can be treated as 'future reservations' so long as the end date
  // has not fully elapsed.
  const isFutureReservation =
    !props.oldReservation && endTime.isAfter(dayjs().endOf('day'));
  

  const orderStatus = {
    cancelled: {
      title: t('Cancelled'),
      class: 'text-red-500',
    },
    processing: {
      title: t('Processing'),
      class: 'text-orange-600',
    },
    confirmed: {
      title: t('Confirmed'),
      class: 'text-green-700',
    },
    queued: {
      title: t('Queued'),
      class: 'text-orange-600',
    },
  };

  let reservationStatus: { title: string; class: string };
  if (props.oldReservation) {
    if (order.State === 'Cancelled' || order.State === 'Queued') {
      reservationStatus = orderStatus.cancelled;
    } else if (order.Confirmed) {
      reservationStatus = orderStatus.confirmed;
    } else {
      reservationStatus = orderStatus.cancelled;
    }
  } else {
    if (order.State === 'Cancelled') {
      reservationStatus = orderStatus.cancelled;
    } else if (order.State === 'Queued') {
      reservationStatus = orderStatus.queued;
    } else if (order.Confirmed) {
      reservationStatus = orderStatus.confirmed;
    } else {
      reservationStatus = orderStatus.processing;
    }
  }

  const dateFormatOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  };

  const startDate = new Date(order.DateTimeStart).toLocaleDateString(
    'fi-FI',
    dateFormatOptions
  );
  const endDate = new Date(order.DateTimeEnd).toLocaleDateString(
    'fi-FI',
    dateFormatOptions
  );
  const orderDoneDate = new Date(order.DoneDate).toLocaleDateString(
    'fi-FI',
    dateFormatOptions
  );

  const drupalBaseUrl: { [key: string]: string } = {};
  drupalBaseUrl['ruka'] = process.env.REACT_APP_DRUPAL_BASE_URL_RUKA ?? '';
  drupalBaseUrl['pyha'] = process.env.REACT_APP_DRUPAL_BASE_URL_PYHA ?? '';

  const productUrl = `${
    drupalBaseUrl[props.order.product.resort.toLowerCase()]
  }/${language}/product/${props.order.product.field_product_id}`;

  const urlParams = `adults=${order.NumAdults}&children=${
    order.NumChildren
  }&infants=${order.NumExtras}&pets=${order.petsChecked ? 1 : 0}`;

  const orderFields = [
    {
      fieldTitle: t('Order number'),
      fieldContent: order.HotellinxReservationLineId,
    },
    {
      fieldTitle: t('Order date'),
      fieldContent: orderDoneDate,
    },
    !order.isPassenger && {
      fieldTitle: t('Payment method'),
      fieldContent:
        order.paymentMethod ?? t('No payment information available'),
    },
    !order.isPassenger &&
      order.Total && {
        fieldTitle: t('Price'),
        fieldContent: `${parseInt(order.Total)},00 €`,
      },
    order.isPassenger &&
      order?.Guests.length > 0 && {
        fieldTitle: t('Main Contact'),
        fieldContent: order.Guests[0].MainCustomer,
      },
    // This field should only be shown in case of owner's reservation.
    !order.isPassenger &&
      ['Ski-Inn pikavaraus', 'Ski-Inn omistajahinta'].includes(
        order.RateName
      ) && {
        fieldTitle: t('Reservation type'),
        fieldContent: t(order.RateName),
        className: 'md:w-full',
      },
    {
      fieldTitle: t('Accommodation'),
      fieldContent: (
        <Link href={productUrl} external={true}>
          <>Ski-Inn {props.order.product.field_title.fi}</>
        </Link>
      ),
    },
    {
      fieldTitle: t('Accommodation date'),
      fieldContent: `${startDate} - ${endDate}`,
    },
    {
      fieldTitle: t('Guests'),
      fieldContent: (
        <>
          <p>
            {t('Adults')}: {order.NumAdults > 0 ? order.NumAdults : '-'}
          </p>
          <p>
            {t('Children 3-11 y/o')}:{' '}
            {order.NumChildren > 0 ? order.NumChildren : '-'}
          </p>
          <p>
            {t('Children 0-2 y/o')}:{' '}
            {order.NumExtras > 0 ? order.NumExtras : '-'}
          </p>
          <p>
            {order.petsChecked && `${t('Pets')}: ${t('Yes', { ns: 'common' })}`}
          </p>
        </>
      ),
    },
    order.extraServices.length > 0 && {
      fieldTitle: t('Extra services'),
      fieldContent: (
        <>
          {order.extraServices.map((extraService, i) => (
            <p key={i}>
              {extraService.Name[language]}{' '}
              {!order.isPassenger &&
                parseFloat(extraService.Price).toFixed(2).replace('.', ',') +
                  ' €'}
            </p>
          ))}
        </>
      ),
    },
    order.PinCode && {
      fieldTitle: t('Pin code'),
      fieldContent: order.PinCode,
    },
    order.State && {
      fieldTitle: t('State of reservation'),
      fieldContent: reservationStatus.title,
    },
    // This field should only be shown in case of owner's reservation.
    ['Ski-Inn pikavaraus', 'Ski-Inn omistajahinta'].includes(order.RateName) &&
      order.Confirmed && {
        fieldTitle: t('Confirmed'),
        fieldContent: order.Confirmed
          ? t('Yes', { ns: 'common' })
          : t('No', { ns: 'common' }),
      },
  ].filter((field) => field);

  return (
    <li>
      <ImageAccordion
        image={{
          src: props.order.product.field_images[0].url,
          alt: t('Reservation image') ?? 'Reservation image',
        }}
        title={
          <div className='lg:flex lg:flex-col align-center justify-center lg:w-7/12 lg:pl-8'>
            <p className='my-3 font-rukaNeue text-xl'>
              {startDate} - {endDate}
            </p>
            <div className='flex justify-between'>
              <h3 className='text-2xl'>
                {' '}
                Ski-Inn {props.order.product.field_title[language]}
              </h3>
            </div>
            {order.isPassenger && (
              <p>{t('You are a fellow passenger on this reservation')}</p>
            )}
            <p className={`${reservationStatus.class}`}>
              {reservationStatus.title}
            </p>
          </div>
        }
      >
        <>
          <ul className='md:flex md:flex-wrap'>
            {orderFields.map(
              (field, i) =>
                field && (
                  <li
                    key={i}
                    className={`my-2 ${field.className ?? 'md:w-6/12'}`}
                  >
                    <p className='font-bold'>{field.fieldTitle}</p>
                    <div>{field.fieldContent}</div>
                  </li>
                )
            )}
          </ul>
          <Button
            className='w-full my-2 md:ml-auto md:w-64'
            external={true}
            variant='transparent'
            href={getDrupalLink(productUrl)}
            icon={props.domain === 'pyha' ? apartmentIconPyha : apartmentIcon}
          >
            {t('To product page') ?? ''}
          </Button>
          <Button
            className='w-full my-2 md:ml-auto md:w-64'
            external={true}
            href={getDrupalLink(`${productUrl}?${urlParams}`)}
          >
            {t('Renew reservation') ?? ''}
          </Button>
          {!order.isPassenger && (
            <>
              <hr className='border-gray-500 my-8 border-1' />
              <FellowPassengers
                incomingGuests={order.Guests}
                hotellinxReservationLineId={order.HotellinxReservationLineId.toString()}
                reservationLineId={order.ReservationLineId}
                customerData={props.customerData}
                numAdults={order.NumAdults}
                idToken={props.idToken}
                isPassenger={order.isPassenger}
                isFutureReservation={isFutureReservation}
                resort={props.order.product.resort}
              />
            </>
          )}

          {/* Filter the widgets that apply to this order. */}
          {props.iTicketWidgets &&
            props.iTicketWidgets.map((widget, i) => {
              if (
                widget &&
                widget.reservationId === props.order.HotellinxReservationLineId
              ) {
                return (
                  <Fragment key={i}>
                    <hr className='border-gray-500 my-8 border-1' />

                    <div className='iticket-widget-container p-4'>
                      <div>
                        <p className='text-2xl md:text-3xl md:mt-4'>
                          {widget.title[language]}
                        </p>

                        {widget.description && widget.description[language] && (
                          <div
                            className={`text-lg widget-description-${domain}`}
                            dangerouslySetInnerHTML={{
                              __html: widget.description[language],
                            }}
                          ></div>
                        )}

                        {widget.deadline && (
                          <p className='font-rukaNeue text-xl mb-4'>
                            {t('Valid until')}{' '}
                            {dayjs(widget.deadline).format('DD.MM.YY')}
                          </p>
                        )}
                      </div>

                      {/* Need to construct a unique id, so each reservation and each placeholder can be targeted. */}
                      <div id={`iticket-widget-placeholder-${i}`}></div>
                    </div>
                  </Fragment>
                );
              }
            })}
        </>
      </ImageAccordion>
    </li>
  );
};

export default ReservationItem;
